<template>
    <div class="Section Partners">
        <div class="SectionHeader">
            <span class="PreTitle">Starke Partner an Ihrer Seite</span>
            <h2 class="MainTitle">Unser Netzwerk</h2>
            <p class="SectionDescription">
                Wir arbeiten mit den führenden Anbietern der Branche zusammen, um Ihnen die beste
                Lösung zu bieten.
            </p>
        </div>

        <div class="PartnerCategories">
            <!-- Taxameter Partners -->
            <div class="PartnerCategory">
                <div class="CategoryHeader">
                    <h3>Taxameter Partner</h3>
                    <span class="CategoryDescription">Zertifizierte Taxameter-Hersteller</span>
                </div>

                <div class="TaxameterGrid">
                    <Card
                        class="TaxameterCard"
                        v-for="(partner, index) in taxameterPartners"
                        :key="index"
                    >
                        <div class="PartnerImageContainer">
                            <img
                                v-if="partner.logo === 'hale.png'"
                                :src="`/partners/${partner.logo}`"
                                :alt="`${partner.name} - TaxiFusion Partner für TSE-konforme Taxameter`"
                                width="120"
                                height="55"
                                loading="lazy"
                            />
                            <img
                                v-else-if="partner.logo === 'semitron.png'"
                                :src="`/partners/${partner.logo}`"
                                :alt="`${partner.name} - TaxiFusion Partner für TSE-konforme Taxameter`"
                                width="120"
                                height="68"
                                loading="lazy"
                            />
                            <img
                                v-else-if="partner.logo === 'digitax.png'"
                                :src="`/partners/${partner.logo}`"
                                :alt="`${partner.name} - TaxiFusion Partner für TSE-konforme Taxameter`"
                                width="120"
                                height="34"
                                loading="lazy"
                            />
                            <img
                                v-else
                                :src="`/partners/${partner.logo}`"
                                :alt="`${partner.name} - TaxiFusion Partner für TSE-konforme Taxameter`"
                                width="120"
                                height="80"
                                loading="lazy"
                            />
                        </div>
                        <div class="TaxameterInfo">
                            <h4>{{ partner.name }}</h4>
                            <span class="TaxameterDescription">{{ partner.description }}</span>
                        </div>
                    </Card>
                </div>
            </div>

            <!-- Umrüster Partners -->
            <div class="PartnerCategory">
                <div class="CategoryHeader">
                    <h3>Umrüster Partner</h3>
                </div>

                <div class="EinbauerCTABanner">
                    <div class="BannerMapPreview">
                        <div class="MapContainer">
                            <l-map
                                v-if="showMap"
                                :zoom="zoom"
                                :center="mapCenter"
                                :minZoom="5"
                                :maxZoom="8"
                                :options="{ zoomControl: false, attributionControl: false }"
                                style="height: 100%; width: 100%"
                            >
                                <l-tile-layer :url="url" :attribution="attribution" />
                                <l-marker
                                    v-for="(location, index) in allEinbauerLocations"
                                    :key="index"
                                    :lat-lng="[location.lat, location.lng]"
                                    :icon="getMarkerIcon(location)"
                                />
                            </l-map>
                        </div>
                        <div class="MapOverlay">
                            <div class="MapBadge">
                                <i class="ri-map-pin-line"></i>
                                <span>{{ einbauerCountDisplay }}+ Einbauer verfügbar</span>
                            </div>
                        </div>
                    </div>
                    <div class="BannerContent">
                        <div class="BannerHeader">
                            <h4>Deutschlandweites Servicenetz</h4>
                            <p>
                                Finden Sie einen zertifizierten Umrüster oder Servicepartner in
                                Ihrer Nähe.
                            </p>
                        </div>
                        <div class="BannerActions">
                            <router-link to="/einbauer" class="PrimaryCTA">
                                <SiteButton>
                                    Einbauer Finden
                                    <i class="ri-arrow-right-line"></i>
                                </SiteButton>
                            </router-link>
                            <div class="SecondaryInfo">
                                <p>
                                    <i class="ri-question-line"></i>
                                    Ist Ihre Werkstatt noch nicht als Partner registriert?
                                </p>
                                <p class="ContactInfo">
                                    Kein Problem, die Freischaltung dauert nur wenige Werktage.
                                    <a href="tel:+4930959982020">Rufen Sie uns an: 030 959982020</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="CategoryHeader">
                <h3>Premium Partner</h3>
            </div>
            <!-- Premium Partner Banner -->
            <div class="PremiumPartnerBanner">
                <div class="BannerContent">
                    <div class="BannerHeader">
                        <h4><strong>myPOS - Premium-Partner für Ihre Taxiflotte</strong></h4>
                        <p>
                            Profitieren Sie von exklusiven Konditionen bei myPOS. Als TaxiFusion
                            Partner erhalten Sie das myPOS Go 2 Terminal
                            <span v-if="showPromotion" class="PriceBadge Free">
                                <i class="ri-gift-line"></i>
                                Wert: 9€ GRATIS
                            </span>
                            <span v-else class="PriceText"> für nur <u>9€ bei TaxiFusion</u> </span
                            >.
                        </p>
                    </div>
                    <div class="BannerActions">
                        <SiteButton
                            variant="success"
                            class="PremiumCTA"
                            @onClick="goToExclusiveOffers"
                        >
                            Alle Angebote ansehen
                            <i class="ri-arrow-right-line"></i>
                        </SiteButton>
                    </div>
                </div>
                <div class="BannerVisual">
                    <div class="VisualContainer">
                        <div class="PartnerImage">
                            <img src="/partners/mypos-lifestyle.jpeg" alt="myPOS Lifestyle" />
                        </div>
                        <div class="VisualBadge">
                            <i class="ri-award-line"></i>
                            <span>Exklusive Partner</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Card from '@/components/Card.vue';
import SiteButton from '@/components/SiteButton.vue';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

export default {
    name: 'Partners',
    components: {
        Card,
        SiteButton,
        LMap,
        LTileLayer,
        LMarker,
    },
    data() {
        return {
            showMap: false,
            mapCenter: [51.1657, 10.4515],
            zoom: 5,
            url: 'http://services.arcgisonline.com/arcgis/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
            attribution: 'Map data &copy; <a href="https://www.esri.com/">Esri</a>',
            taxameterPartners: [
                {
                    logo: 'hale.png',
                    name: 'Hale',
                },
                {
                    logo: 'semitron.png',
                    name: 'Semitron',
                },
                {
                    logo: 'digitax.png',
                    name: 'Digitax',
                },
            ],
            umruesterPartners: [
                {
                    logo: 'heedfeld.png',
                    location: 'Berlin',
                    link: 'https://www.heedfeld-berlin.de/kontakt/',
                },
                {
                    logo: 'hico.png',
                    location: 'Berlin',
                    link: 'https://www.hico-berlin.de',
                },
                {
                    logo: 'kraft.png',
                    location: 'Berlin',
                    link: 'https://www.taxameter-kraft.de/',
                },
                {
                    logo: 'koeln.png',
                    location: 'Köln',
                    link: 'https://www.taxikoeln.eu/contact.html',
                },
            ],
        };
    },
    computed: {
        ...mapGetters('promotion', ['showPromotion']),
        totalEinbauerCount() {
            return this.allEinbauerLocations.length;
        },
        einbauerCountDisplay() {
            const count = this.totalEinbauerCount;
            if (count < 10) {
                return Math.floor(count / 5) * 5;
            } else if (count < 50) {
                return Math.floor(count / 10) * 10;
            } else if (count < 100) {
                return Math.floor(count / 25) * 25;
            } else {
                return Math.floor(count / 50) * 50;
            }
        },
        allEinbauerLocations() {
            return [
                { lat: 52.4107969, lng: 13.3810642, partners: ['digitax'] },
                { lat: 52.5285208, lng: 13.527243, partners: ['hale', 'semitron'] },
                { lat: 52.4938, lng: 13.3877, partners: ['hale', 'semitron'] },
                { lat: 52.5285844, lng: 13.5253093, partners: ['hale', 'semitron'] },
                { lat: 52.4617865, lng: 13.3329934, partners: ['hale'] },
                { lat: 52.5871884, lng: 13.3687514, partners: ['hale'] },
                { lat: 52.518674, lng: 13.5219183, partners: ['semitron'] },
                { lat: 52.4137703, lng: 12.5168945, partners: ['hale'] },
                { lat: 48.29539, lng: 11.6200638, partners: ['hale'] },
                { lat: 48.1884545, lng: 11.5827044, partners: ['hale', 'semitron'] },
                { lat: 48.4304142, lng: 11.9788054, partners: ['hale'] },
                { lat: 48.1153, lng: 11.5347, partners: ['hale'] },
                { lat: 48.1351, lng: 11.582, partners: ['semitron'] },
                { lat: 48.8006508, lng: 9.2269992, partners: ['hale', 'semitron'] },
                { lat: 50.9180986, lng: 6.9515036, partners: ['hale'] },
                { lat: 50.9375, lng: 6.9603, partners: ['semitron'] },
                { lat: 51.2061284, lng: 6.8256981, partners: ['hale'] },
                { lat: 50.0680343, lng: 8.3808377, partners: ['hale'] },
                { lat: 51.3397, lng: 12.3731, partners: ['semitron'] },
                { lat: 50.7189, lng: 12.4927, partners: ['semitron'] },
                { lat: 53.0793, lng: 8.8017, partners: ['semitron'] },
                { lat: 54.0924, lng: 12.0991, partners: ['semitron'] },
                { lat: 53.6353, lng: 11.4167, partners: ['semitron'] },
                { lat: 53.5511, lng: 9.9937, partners: ['semitron'] },
                { lat: 53.6182017, lng: 10.0323564, partners: ['digitax'] },
                { lat: 53.5853076, lng: 9.9281756, partners: ['digitax', 'hale', 'semitron'] },
                { lat: 53.8958, lng: 10.6471, partners: ['semitron'] },
                { lat: 54.5558, lng: 9.3886, partners: ['semitron'] },
                { lat: 53.18, lng: 8.0167, partners: ['semitron'] },
                { lat: 53.2333, lng: 7.45, partners: ['semitron'] },
                { lat: 52.4333, lng: 9.8333, partners: ['semitron'] },
                { lat: 51.5833, lng: 9.9167, partners: ['hale', 'semitron'] },
                { lat: 51.3833, lng: 10.1333, partners: ['semitron'] },
                { lat: 51.25, lng: 6.3833, partners: ['semitron'] },
                { lat: 51.5167, lng: 7.45, partners: ['semitron'] },
                { lat: 51.4333, lng: 6.8833, partners: ['semitron'] },
                { lat: 51.9667, lng: 7.6167, partners: ['semitron'] },
                { lat: 52.4333, lng: 7.0667, partners: ['semitron'] },
                { lat: 52.2833, lng: 8.05, partners: ['semitron'] },
                { lat: 50.4333, lng: 7.4667, partners: ['semitron'] },
                { lat: 50.8667, lng: 8.0167, partners: ['hale', 'semitron'] },
                { lat: 50.1109, lng: 8.6821, partners: ['semitron'] },
                { lat: 49.2333, lng: 7.0, partners: ['semitron'] },
                { lat: 49.4833, lng: 8.4667, partners: ['semitron'] },
                { lat: 49.3833, lng: 8.6167, partners: ['semitron'] },
                { lat: 49.1333, lng: 9.2167, partners: ['semitron'] },
                { lat: 48.0667, lng: 8.5333, partners: ['semitron'] },
                { lat: 47.9833, lng: 7.8333, partners: ['semitron'] },
                { lat: 48.35, lng: 10.9833, partners: ['semitron'] },
                { lat: 47.7833, lng: 10.6167, partners: ['semitron'] },
                { lat: 49.5833, lng: 11.0167, partners: ['semitron'] },
                { lat: 49.3, lng: 10.5667, partners: ['semitron'] },
                { lat: 49.45, lng: 11.85, partners: ['semitron'] },
                { lat: 48.55, lng: 12.8167, partners: ['hale', 'semitron'] },
                { lat: 50.05, lng: 10.2333, partners: ['semitron'] },
                { lat: 50.9833, lng: 11.0333, partners: ['semitron'] },
                { lat: 51.85, lng: 6.8667, partners: ['semitron'] },
                { lat: 51.1833, lng: 6.5167, partners: ['semitron'] },
                { lat: 52.0333, lng: 6.8167, partners: ['semitron'] },
                { lat: 47.7667, lng: 11.5667, partners: ['semitron'] },
                { lat: 50.3167, lng: 10.8833, partners: ['semitron'] },
                { lat: 48.3667, lng: 10.85, partners: ['semitron'] },
                { lat: 48.8, lng: 8.3167, partners: ['semitron'] },
                { lat: 51.1657, lng: 10.4515, partners: ['hale', 'digitax'] },
            ];
        },
    },
    methods: {
        goToExclusiveOffers() {
            this.$router.push('/exclusive-offers');
        },
        getMarkerIcon(location) {
            let color = '#6b7280';

            if (
                location.partners.includes('hale') &&
                location.partners.includes('semitron') &&
                location.partners.includes('digitax')
            ) {
                color = '#8b5cf6';
            } else if (
                location.partners.includes('hale') &&
                location.partners.includes('digitax')
            ) {
                color = '#dc2626';
            } else if (
                location.partners.includes('hale') &&
                location.partners.includes('semitron')
            ) {
                color = '#8b5cf6';
            } else if (
                location.partners.includes('semitron') &&
                location.partners.includes('digitax')
            ) {
                color = '#7c3aed';
            } else if (location.partners.includes('hale')) {
                color = '#10b981';
            } else if (location.partners.includes('semitron')) {
                color = '#3b82f6';
            } else if (location.partners.includes('digitax')) {
                color = '#dc2626';
            }

            return new L.Icon({
                iconUrl:
                    'data:image/svg+xml;base64,' +
                    btoa(`
                    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 12.5 12.5 28.5 12.5 28.5S25 25 25 12.5C25 5.596 19.404 0 12.5 0z" fill="${color}"/>
                        <circle cx="12.5" cy="12.5" r="5" fill="#ffffff"/>
                    </svg>
                `),
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
            });
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.showMap = true;
        });
    },
};
</script>

<style lang="scss">
.Partners {
    padding: 120px 75px;
    position: relative;
    overflow: hidden;
    @extend %sectionWrapper;

    @media (max-width: $breakpoint-tablet-portrait) {
        padding: 60px 20px;
    }

    .SectionHeader {
        margin-bottom: 80px;
        position: relative;
        text-align: left;

        @media (max-width: $breakpoint-tablet-portrait) {
            margin-bottom: 60px;
        }
    }

    .SectionTitle {
        font-size: 2.5rem;
        font-weight: 700;
        color: #161936;
        margin-bottom: 1rem;
        line-height: 1.2;
        text-align: left;

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 2rem;
        }

        @media (max-width: $breakpoint-mobile) {
            font-size: 1.75rem;
        }
    }

    .SectionSubtitle {
        font-size: 1.25rem;
        color: #64748b;
        line-height: 1.6;
        max-width: 600px;
        margin: 0;
        text-align: left;
        font-weight: 400;

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 1.1rem;
        }

        @media (max-width: $breakpoint-mobile) {
            font-size: 1rem;
        }
    }

    .PreTitle {
        display: block;
        font-size: 1.125rem;
        font-weight: 500;
        letter-spacing: 0.05em;
        color: var(--color-blue-dark);
        margin-bottom: 12px;
    }

    .MainTitle {
        font-size: 3.5rem;
        font-weight: 700;
        margin: 0 0 18px;
        background: linear-gradient(120deg, black, var(--color-blue-light));
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        text-align: left;

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 2.5rem;
        }
    }

    .SectionDescription {
        max-width: 550px;
        font-size: 1.2rem;
        color: #4b5563;
        line-height: 1.6;
        text-align: left;
        margin: 0;
    }

    .PartnerCategories {
        margin: 0 auto;
    }

    .PremiumPartnerBanner {
        display: grid;
        grid-template-columns: 60% 40%;
        gap: 0;
        background: white;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        margin: 20px 0 30px;
        border: 0.5px solid rgba(0, 0, 0, 0.05);

        @media (max-width: $breakpoint-tablet-portrait) {
            grid-template-columns: 1fr;
        }

        .BannerContent {
            padding: 40px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            @media (max-width: $breakpoint-tablet-portrait) {
                padding: 30px 20px;
            }

            .BannerHeader {
                margin-bottom: 30px;

                h4 {
                    font-size: 1.75rem;
                    color: #2d3748;
                    margin: 0 0 12px;
                    line-height: 1.3;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        font-size: 1.5rem;
                    }
                }

                p {
                    font-size: 1.1rem;
                    color: #64748b;
                    line-height: 1.6;
                    margin: 0 0 20px;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        font-size: 1rem;
                    }

                    .PriceText {
                        display: inline;
                        color: #2d3748;
                        font-weight: 500;

                        u {
                            text-decoration: underline;
                            text-decoration-thickness: 2px;
                            text-underline-offset: 2px;
                        }
                    }

                    .PriceBadge {
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                        color: white;
                        padding: 8px 16px;
                        border-radius: 8px;
                        font-weight: 700;
                        font-size: 1rem;
                        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
                        margin-left: 4px;

                        i {
                            font-size: 1rem;
                        }

                        @media (max-width: $breakpoint-tablet-portrait) {
                            font-size: 0.9rem;
                            padding: 6px 12px;
                        }
                    }
                }
            }

            .BannerActions {
                display: flex;
                flex-direction: column;
                gap: 20px;

                .PremiumCTA {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 28px;
                    font-size: 1.1rem;
                    font-weight: 400;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                    align-self: flex-start;

                    :deep(span) {
                        font-weight: 400;
                    }

                    i {
                        font-size: 1.1rem;
                    }
                }
            }
        }

        .BannerVisual {
            position: relative;
            min-height: 300px;
            background: #f8fafc;
            overflow: hidden;

            @media (max-width: $breakpoint-tablet-portrait) {
                min-height: 250px;
            }

            .VisualContainer {
                width: 100%;
                height: 100%;
                position: relative;
                z-index: 1;

                .PartnerImage {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }
                }

                .VisualBadge {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    padding: 10px 16px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.75rem;
                    font-weight: 400;
                    color: var(--color-blue-dark);
                    z-index: 2;
                    pointer-events: auto;

                    i {
                        font-size: 1.1rem;
                        color: #ffd700;
                    }
                }
            }
        }
    }

    .EinbauerCTABanner {
        display: grid;
        grid-template-columns: 40% 60%;
        gap: 0;
        background: white;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        margin: 20px 0 30px;
        border: 0.5px solid rgba(0, 0, 0, 0.05);

        @media (max-width: $breakpoint-tablet-portrait) {
            grid-template-columns: 1fr;
        }

        .BannerMapPreview {
            position: relative;
            min-height: 300px;
            background: #f8fafc;
            overflow: hidden;

            @media (max-width: $breakpoint-tablet-portrait) {
                min-height: 250px;
            }

            .MapContainer {
                width: 100%;
                height: 100%;
                position: relative;
                z-index: 1;

                :deep(.leaflet-container) {
                    background: #1a1a1a;
                    font-family: inherit;
                }

                :deep(.leaflet-control-attribution) {
                    display: none;
                }

                :deep(.leaflet-control-zoom) {
                    display: none !important;
                }
            }

            .MapOverlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                z-index: 2;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
                padding: 20px;

                .MapBadge {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    padding: 10px 16px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.75rem;
                    font-weight: 400;
                    color: var(--color-blue-dark);
                    pointer-events: auto;

                    i {
                        font-size: 1.1rem;
                        color: #34d399;
                    }
                }
            }
        }

        .BannerContent {
            padding: 40px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            @media (max-width: $breakpoint-tablet-portrait) {
                padding: 30px 20px;
            }

            .BannerHeader {
                margin-bottom: 30px;

                h4 {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #2d3748;
                    margin: 0 0 12px;
                    line-height: 1.3;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        font-size: 1.5rem;
                    }
                }

                p {
                    font-size: 1.1rem;
                    color: #64748b;
                    line-height: 1.6;
                    margin: 0;

                    @media (max-width: $breakpoint-tablet-portrait) {
                        font-size: 1rem;
                    }
                }
            }

            .BannerActions {
                display: flex;
                flex-direction: column;
                gap: 20px;

                .PrimaryCTA {
                    text-decoration: none;
                    display: inline-block;
                    align-self: flex-start;

                    :deep(.SiteButton) {
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        padding: 14px 28px;
                        font-size: 1.1rem;
                        font-weight: 600;
                        background: var(--color-blue-dark);
                        color: white;
                        border: none;
                        border-radius: 8px;
                        transition: all 0.3s ease;
                        box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);

                        &:hover {
                            transform: translateY(-2px);
                            box-shadow: 0 6px 20px rgba(30, 58, 138, 0.4);
                            background: #1e3a8a;
                        }

                        i {
                            font-size: 1.1rem;
                        }
                    }
                }

                .SecondaryInfo {
                    p {
                        font-size: 0.95rem;
                        color: #64748b;
                        margin: 0 0 8px;
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        i {
                            color: #34d399;
                            font-size: 1rem;
                        }

                        &.ContactInfo {
                            color: #475569;
                            font-weight: 500;

                            a {
                                color: var(--color-blue-dark);
                                text-decoration: underline;
                                transition: color 0.2s ease;

                                &:hover {
                                    opacity: 0.8;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .PartnerDescription {
        font-size: 1.4rem;
        color: #2d3748;
        line-height: 1.7;
        font-weight: 500;
        margin: 20px 0 30px;
        padding: 50px;
        border-left: 4px solid var(--color-blue-dark);
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

        a {
            color: var(--color-blue-dark);
            text-decoration: underline;
            transition: color 0.2s ease;

            &:hover {
                color: var(--color-blue-dark);
                opacity: 0.8;
            }
        }

        @media (max-width: $breakpoint-tablet-portrait) {
            font-size: 1.2rem;
            padding: 20px;
            margin-bottom: 20px;
        }
    }

    .PartnerCategory {
        margin-bottom: 80px;

        &:last-child {
            margin-bottom: 40px;
        }
    }

    .CategoryHeader {
        margin-bottom: 30px;

        h3 {
            font-weight: 700;
            margin-bottom: 8px;
            color: #2d3748;
        }

        .CategoryDescription {
            font-size: 1.125rem;
            color: #4b5563;
        }
    }

    .TaxameterGrid {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 20px;
    }

    .TaxameterCard {
        background: white;
        border-radius: 16px;
        padding: 30px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-10px);
            box-shadow: 0 15px 35px rgba(var(--color-blue-dark-rgb), 0.15);

            .PartnerImageContainer img {
                filter: none;
            }
        }

        .PartnerImageContainer {
            background: #f8fafc;
            width: 120px;
            height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            margin-bottom: 20px;
            overflow: hidden;
            border: 0.5px solid #e2e8f0;

            img {
                max-width: 80%;
                max-height: 60px;
                filter: grayscale(0.4);
            }
        }

        .TaxameterInfo {
            h4 {
                font-weight: 600;
                margin: 0 0 10px;
                color: #2d3748;
            }

            .TaxameterDescription {
                display: block;
                color: #64748b;
                font-size: 0.95rem;
            }
        }
    }

    .PartnerGrid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 20px;
        @media (max-width: $breakpoint-mobile) {
            grid-template-columns: 1fr;
        }
    }

    .PartnerCard {
        background: white;
        border-radius: 16px;
        padding: 30px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        height: 100%;
        border: 0.5px solid #e2e8f0;
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-10px);
            box-shadow: 0 15px 35px rgba(var(--color-blue-dark-rgb), 0.15);

            .PartnerImageContainer img {
                filter: none;
                transform: scale(1.05);
            }

            .AppointmentButton {
                background-color: var(--color-blue-dark);
                color: white;

                svg {
                    transform: translateX(5px);
                }
            }
        }

        .PartnerImageContainer {
            height: 80px;
            display: flex;
            align-items: center;
            margin-bottom: 20px;

            img {
                max-width: 120px;
                filter: grayscale(0.4);
            }
        }

        .PartnerContent {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        }

        h4 {
            font-weight: 600;
            margin: 0 0 10px;
            color: #2d3748;
            font-size: 1.4rem;
        }

        p {
            color: #64748b;
            margin-bottom: 20px;
            flex-grow: 1;
            line-height: 1.6;
        }
    }

    .AppointmentButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 12px 20px;
        border-radius: 8px;
        text-decoration: none;
        font-weight: 600;
        border: 0.5px solid var(--color-blue-dark);
        color: var(--color-blue-dark);
        align-self: flex-start;

        svg {
            margin-left: 8px;
        }
    }

    .ExclusiveOffersTeaser {
        background: linear-gradient(135deg, #161936 0%, #181928 100%);
        border: 0.5px solid rgba(255, 255, 255, 0.08);
        border-radius: 16px;
        backdrop-filter: blur(10px);
        padding: 2rem;
        margin-bottom: 2rem;
        transition: all 0.3s ease;

        &:hover {
            background: linear-gradient(135deg, #1a1a3a 0%, #1e1e32 100%);
            border-color: rgba(255, 255, 255, 0.12);
            transform: translateY(-2px);
        }

        @media (max-width: $breakpoint-mobile) {
            padding: 1.5rem;
        }
    }

    .TeaserContent {
        display: flex;
        align-items: center;
        gap: 1.5rem;

        @media (max-width: $breakpoint-mobile) {
            flex-direction: column;
            gap: 1rem;
        }
    }

    .TeaserIcon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, #ffd700, #ffed4e);
        border-radius: 50%;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);

        i {
            font-size: 1.5rem;
            color: #161936;
        }

        @media (max-width: $breakpoint-mobile) {
            width: 50px;
            height: 50px;

            i {
                font-size: 1.25rem;
            }
        }
    }

    .TeaserText {
        flex: 1;

        h3 {
            font-size: 1.5rem;
            font-weight: 700;
            color: white;
            margin: 0 0 0.5rem 0;
            line-height: 1.3;

            @media (max-width: $breakpoint-mobile) {
                font-size: 1.25rem;
            }
        }

        p {
            color: rgba(255, 255, 255, 0.85);
            font-size: 1rem;
            line-height: 1.6;
            margin: 0;
            max-width: 400px;

            @media (max-width: $breakpoint-mobile) {
                font-size: 0.95rem;
            }
        }
    }

    .TeaserButton {
        flex-shrink: 0;
        text-decoration: none;

        .Button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            font-size: 0.9rem;
            font-weight: 600;
            border-radius: 8px;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-2px);
            }

            i {
                font-size: 14px;
                transition: transform 0.3s ease;
            }

            &:hover i {
                transform: translateX(3px);
            }
        }

        @media (max-width: $breakpoint-mobile) {
            width: 100%;

            .Button {
                width: 100%;
                justify-content: center;
            }
        }
    }

    .SpecialOfferContainer {
        margin-top: 20px;
    }

    .PromoBenefits {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 20px;

        .PromoBenefit {
            display: flex;
            align-items: center;
            gap: 8px;
            background: rgba(251, 191, 36, 0.15);
            border: 0.5px solid var(--color-warning);
            padding: 8px 15px;
            border-radius: 50px;

            i {
                color: #7c2d12;
                font-size: 16px;
            }

            span {
                font-weight: 600;
                color: #7c2d12;
                font-size: 0.9rem;
            }
        }
    }
    .PromoPriceTag {
        position: absolute;
        top: -15px;
        right: -15px;
        background: #ef4444;
        color: white;
        font-weight: 800;
        font-size: 1.2rem;
        padding: 10px 20px;
        border-radius: 50px;
        box-shadow: 0 5px 15px rgba(239, 68, 68, 0.4);
        transform: rotate(15deg);
        z-index: 3;
    }

    .SpecialOffer {
        border-radius: 20px;
        background: linear-gradient(135deg, white 50%, #ffccad);
        overflow: hidden;
        box-shadow: 0 20px 40px rgba(251, 191, 36, 0.15);
        border: 0.5px solid var(--color-warning);
        position: relative;

        .OfferBadge {
            position: absolute;
            top: 15px;
            right: 15px;
            background: var(--color-warning);
            padding: 8px 18px 8px 12px;
            border-radius: 30px;
            font-size: 0.95rem;
            font-weight: 700;
            color: #7c2d12;
            display: flex;
            align-items: center;
            gap: 5px;
            box-shadow: 0 4px 10px rgba(251, 191, 36, 0.3);
            background: #ef4444;
            color: white;

            span {
                font-weight: 800;
                margin-right: 5px;
            }

            @media (max-width: $breakpoint-mobile) {
                position: relative;
                width: calc(100% - 20px);
                flex-direction: column;
                gap: 0px;
                left: 0;
                top: 10px;
                margin: 0 auto;
                border-radius: 16px;
            }
        }

        .OfferContent {
            display: flex;
            padding: 50px;
            align-items: center;

            @media (max-width: $breakpoint-tablet-landscape) {
                flex-direction: column-reverse;
                padding: 40px 20px;
            }
        }

        .OfferInfo {
            flex: 1;
            padding-right: 40px;

            @media (max-width: $breakpoint-tablet-landscape) {
                padding-right: 0;
                margin-bottom: 40px;
            }

            .PartnerLogo {
                margin-bottom: 25px;

                img {
                    height: 60px;
                }
            }

            h4 {
                font-weight: 700;
                margin: 0 0 20px;
                color: #2d3748;
                font-size: 1.5rem;
            }

            .OfferDescription {
                margin-bottom: 30px;

                p {
                    color: #4b5563;
                    font-size: 1.1rem;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
            }

            .BenefitsList {
                list-style: none;
                padding: 0;
                margin: 0 0 30px;

                li {
                    display: flex;
                    align-items: center;
                    margin-bottom: 12px;
                    color: #4b5563;
                    font-size: 1.05rem;

                    svg {
                        color: var(--color-warning);
                        margin-right: 10px;
                        flex-shrink: 0;
                    }

                    @media (max-width: $breakpoint-tablet-landscape) {
                        justify-content: center;
                    }
                }
            }
        }

        .ProductImage {
            flex: 1;
            display: flex;
            justify-content: center;
            position: relative;

            img {
                max-height: 300px;
                filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.15));
                transform: perspective(800px) rotateY(-15deg) rotateX(5deg) rotate(5deg);
                transition: transform 0.5s ease;

                &:hover {
                    transform: perspective(800px) rotateY(0deg) rotateX(0deg) rotate(0deg);
                }

                @media (max-width: $breakpoint-tablet-landscape) {
                    max-width: 280px;
                    transform: none;
                }
            }
        }
    }
    .LimitedOfferBadge {
        position: absolute;
        top: 15px;
        left: 15px;
        background: #ef4444;
        padding: 8px 18px 8px 12px;
        border-radius: 30px;
        font-size: 0.95rem;
        font-weight: 700;
        color: white;
        display: flex;
        align-items: center;
        gap: 5px;
        box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);
        z-index: 2;

        svg {
            color: white;
        }
    }

    .SpecialOfferButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 16px 30px;
        border-radius: 12px;
        text-decoration: none;
        font-weight: 700;
        background-color: var(--color-warning);
        color: #7c2d12;
        font-size: 1.2rem;
        box-shadow:
            0 10px 20px rgba(251, 191, 36, 0.3),
            0 3px 6px rgba(251, 191, 36, 0.2);
        transition: all 0.3s ease;

        span {
            margin-right: 10px;
        }

        svg {
            transition: transform 0.3s ease;
        }

        &:hover {
            transform: translateY(-3px);
            box-shadow:
                0 15px 25px rgba(251, 191, 36, 0.35),
                0 5px 10px rgba(251, 191, 36, 0.2);

            svg {
                transform: translateX(5px);
            }
        }

        @media (max-width: $breakpoint-tablet-landscape) {
            margin: 0 auto;
        }
    }

    .PartnerCTA {
        margin-top: 60px;
        background: linear-gradient(135deg, var(--color-blue-dark), var(--color-blue-light));
        border-radius: 16px;
        padding: 40px;

        color: white;
        box-shadow: 0 15px 30px rgba(var(--color-blue-dark-rgb), 0.2);

        p {
            font-size: 1.3rem;
            margin-bottom: 25px;
            font-weight: 500;
        }

        .PartnershipButton {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 16px 30px;
            border-radius: 12px;
            text-decoration: none;
            font-weight: 700;
            background-color: white;
            color: var(--color-blue-dark);
            font-size: 1.1rem;
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);

            &:hover {
                transform: translateY(-3px);
                box-shadow: 0 15px 25px rgba(0, 0, 0, 0.15);
                background-color: var(--color-warning);
                color: #7c2d12;
            }
        }
    }
}
</style>
